import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useManagerDashboard } from '@/hooks/useManagerDashboard';
import { useCanActAsManager } from '@/hooks/useCanActAsManager';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell } from 'recharts';
import { 
  ArrowLeft, 
  AlertTriangle, 
  AlertCircle, 
  Users, 
  CheckCircle2, 
  Clock, 
  TrendingUp,
  RefreshCw,
  Loader2,
  UserX,
  MessageSquareWarning,
  Timer,
  ArrowUpDown
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DashboardFilters, DashboardAlert, AgentStats } from '@/types/support.types';

const CHART_COLORS = [
  'hsl(210, 70%, 50%)',
  'hsl(160, 60%, 45%)',
  'hsl(340, 65%, 50%)',
  'hsl(45, 80%, 50%)',
  'hsl(280, 55%, 55%)',
  'hsl(20, 70%, 50%)',
];

type SortKey = 'total_sessions' | 'active_sessions' | 'resolved_period' | 'sla_compliance' | 'avg_response_time' | 'avg_resolution_min' | 'sla_breached' | 'distribution_percent';

export default function ManagerDashboard() {
  const navigate = useNavigate();
  const { canActAsManager, isLoading: roleLoading } = useCanActAsManager();
  const [filters, setFilters] = useState<DashboardFilters>({ period: 'today' });
  const [sortKey, setSortKey] = useState<SortKey>('total_sessions');
  const [sortAsc, setSortAsc] = useState(false);
  
  const { 
    metrics, 
    metricsLoading, 
    alerts, 
    alertsLoading,
    refetch 
  } = useManagerDashboard({ filters });

  if (!roleLoading && !canActAsManager) {
    navigate('/customer-service');
    return null;
  }

  const getAlertIcon = (type: DashboardAlert['type']) => {
    switch (type) {
      case 'sla_breach':
        return <AlertCircle className="h-4 w-4" />;
      case 'pending_escalation':
        return <Timer className="h-4 w-4" />;
      case 'unassigned':
        return <UserX className="h-4 w-4" />;
      case 'multiple_unanswered':
        return <MessageSquareWarning className="h-4 w-4" />;
      default:
        return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const formatMinutes = (minutes: number | null): string => {
    if (minutes === null) return '-';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  const periodLabels = {
    today: 'Hoje',
    week: 'Últimos 7 dias',
    month: 'Últimos 30 dias',
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
  };

  const sortedAgents = [...(metrics?.by_agent || [])].sort((a, b) => {
    const aVal = a[sortKey] ?? -Infinity;
    const bVal = b[sortKey] ?? -Infinity;
    return sortAsc ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
  });

  // Chart data
  const chartData = sortedAgents.map((agent, i) => ({
    name: agent.agent_name.split(' ')[0],
    ativas: agent.active_sessions,
    resolvidas: agent.resolved_period,
    total: agent.total_sessions,
    fill: CHART_COLORS[i % CHART_COLORS.length],
  }));

  const chartConfig = {
    ativas: { label: 'Ativas', color: 'hsl(210, 70%, 50%)' },
    resolvidas: { label: 'Resolvidas', color: 'hsl(160, 60%, 45%)' },
  };

  const SortHeader = ({ label, field, className }: { label: string; field: SortKey; className?: string }) => (
    <TableHead 
      className={cn("cursor-pointer select-none hover:bg-muted/50", className)}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1 justify-center">
        {label}
        <ArrowUpDown className={cn("h-3 w-3", sortKey === field ? "text-foreground" : "text-muted-foreground/50")} />
      </div>
    </TableHead>
  );

  const getResolutionRateBadge = (agent: AgentStats) => {
    if (agent.total_sessions === 0) return <span className="text-muted-foreground">-</span>;
    const rate = Math.round((agent.resolved_period / agent.total_sessions) * 100);
    return (
      <Badge variant="outline" className={cn(
        rate >= 80 ? "border-emerald-500 text-emerald-600" :
        rate >= 50 ? "border-amber-500 text-amber-600" :
        "border-destructive text-destructive"
      )}>
        {rate}%
      </Badge>
    );
  };

  return (
    <div className="bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/customer-service')}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-xl font-semibold">Dashboard de Atendimento</h1>
                <p className="text-sm text-muted-foreground">
                  Visão geral e métricas do Customer Service
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select
                value={filters.period}
                onValueChange={(value) => setFilters({ ...filters, period: value as DashboardFilters['period'] })}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Hoje</SelectItem>
                  <SelectItem value="week">Últimos 7 dias</SelectItem>
                  <SelectItem value="month">Últimos 30 dias</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={refetch}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Alerts Section */}
        {alerts.length > 0 && (
          <div className="space-y-2">
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              Alertas
            </h2>
            <div className="flex flex-wrap gap-2">
              {alertsLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                alerts.map((alert) => (
                  <Badge
                    key={alert.id}
                    variant="outline"
                    className={cn(
                      "cursor-pointer px-3 py-1.5 text-sm gap-2",
                      alert.severity === 'critical' 
                        ? "border-destructive bg-destructive/10 text-destructive hover:bg-destructive/20" 
                        : "border-amber-500 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-950/20 dark:text-amber-400"
                    )}
                    onClick={() => navigate('/customer-service')}
                  >
                    {getAlertIcon(alert.type)}
                    <span>{alert.count} {alert.title}</span>
                  </Badge>
                ))
              )}
            </div>
          </div>
        )}

        {/* Metrics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <MetricCard
            title="Sessões Ativas"
            value={metrics?.total_active ?? '-'}
            icon={<Users className="h-4 w-4" />}
            loading={metricsLoading}
            subtitle={`${metrics?.total_new ?? 0} novas`}
          />
          <MetricCard
            title="Resolvidas"
            value={
              filters.period === 'today' ? (metrics?.total_resolved_today ?? '-') :
              filters.period === 'week' ? (metrics?.total_resolved_week ?? '-') :
              (metrics?.total_resolved_month ?? '-')
            }
            icon={<CheckCircle2 className="h-4 w-4" />}
            loading={metricsLoading}
            subtitle={periodLabels[filters.period]}
            variant="success"
          />
          <MetricCard
            title="% Dentro SLA"
            value={metrics ? `${metrics.sla_compliance_percent}%` : '-'}
            icon={<TrendingUp className="h-4 w-4" />}
            loading={metricsLoading}
            variant={
              metrics?.sla_compliance_percent === undefined ? 'default' :
              metrics.sla_compliance_percent >= 90 ? 'success' :
              metrics.sla_compliance_percent >= 70 ? 'warning' : 'danger'
            }
          />
          <MetricCard
            title="Tempo Médio 1ª Resp"
            value={formatMinutes(metrics?.avg_first_response_min ?? null)}
            icon={<Clock className="h-4 w-4" />}
            loading={metricsLoading}
          />
          <MetricCard
            title="Sem Atendente"
            value={metrics?.unassigned_count ?? '-'}
            icon={<UserX className="h-4 w-4" />}
            loading={metricsLoading}
            variant={(metrics?.unassigned_count ?? 0) > 0 ? 'warning' : 'default'}
          />
        </div>

        {/* Distribution Chart */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium">
              Distribuição de Chamados por Atendente
            </CardTitle>
          </CardHeader>
          <CardContent>
            {metricsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : chartData.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum dado disponível
              </div>
            ) : (
              <div className="space-y-4">
                <ChartContainer config={chartConfig} className="h-[300px] w-full">
                  <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="name" className="text-xs" />
                    <YAxis className="text-xs" />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="ativas" stackId="a" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="resolvidas" stackId="a" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ChartContainer>

                {/* Distribution pills */}
                <div className="flex flex-wrap gap-2">
                  {sortedAgents.map((agent, i) => (
                    <Badge key={agent.agent_id} variant="outline" className="gap-1.5 px-3 py-1">
                      <span
                        className="w-2.5 h-2.5 rounded-full inline-block"
                        style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
                      />
                      {agent.agent_name.split(' ')[0]} — {agent.distribution_percent}% ({agent.total_sessions})
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Agents Performance Table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium">
              Desempenho por Atendente
            </CardTitle>
          </CardHeader>
          <CardContent>
            {metricsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : sortedAgents.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum atendente com sessões no período
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Atendente</TableHead>
                      <SortHeader label="Total" field="total_sessions" />
                      <SortHeader label="Ativas" field="active_sessions" />
                      <SortHeader label="Resolvidas" field="resolved_period" />
                      <SortHeader label="Taxa Resolução" field="resolved_period" />
                      <SortHeader label="% SLA" field="sla_compliance" />
                      <SortHeader label="SLA Violados" field="sla_breached" />
                      <SortHeader label="Tempo 1ª Resp" field="avg_response_time" />
                      <SortHeader label="Tempo Resolução" field="avg_resolution_min" />
                      <SortHeader label="Distribuição" field="distribution_percent" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedAgents.map((agent) => (
                      <TableRow key={agent.agent_id}>
                        <TableCell className="font-medium whitespace-nowrap">{agent.agent_name}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant="secondary">{agent.total_sessions}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">{agent.active_sessions}</Badge>
                        </TableCell>
                        <TableCell className="text-center">{agent.resolved_period}</TableCell>
                        <TableCell className="text-center">{getResolutionRateBadge(agent)}</TableCell>
                        <TableCell className="text-center">
                          <Badge 
                            variant="outline"
                            className={cn(
                              agent.sla_compliance >= 90 ? "border-emerald-500 text-emerald-600" :
                              agent.sla_compliance >= 70 ? "border-amber-500 text-amber-600" :
                              "border-destructive text-destructive"
                            )}
                          >
                            {agent.sla_compliance}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {agent.sla_breached > 0 ? (
                            <Badge variant="outline" className="border-destructive text-destructive">
                              {agent.sla_breached}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">0</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center text-muted-foreground">
                          {formatMinutes(agent.avg_response_time)}
                        </TableCell>
                        <TableCell className="text-center text-muted-foreground">
                          {formatMinutes(agent.avg_resolution_min)}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="secondary">{agent.distribution_percent}%</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Metric Card Component
interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  loading?: boolean;
  subtitle?: string;
  variant?: 'default' | 'success' | 'warning' | 'danger';
}

function MetricCard({ title, value, icon, loading, subtitle, variant = 'default' }: MetricCardProps) {
  const variantStyles = {
    default: 'bg-card',
    success: 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800',
    warning: 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800',
    danger: 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800',
  };

  const iconStyles = {
    default: 'text-muted-foreground',
    success: 'text-emerald-600 dark:text-emerald-400',
    warning: 'text-amber-600 dark:text-amber-400',
    danger: 'text-red-600 dark:text-red-400',
  };

  return (
    <Card className={cn("transition-colors", variantStyles[variant])}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {title}
          </span>
          <span className={iconStyles[variant]}>{icon}</span>
        </div>
        {loading ? (
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        ) : (
          <>
            <p className="text-2xl font-bold">{value}</p>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
